import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { AxiosServiceResponse, PagedList } from 'interfaces/general';

const instance = axios.create({
  baseURL: `${process.env.INVENTORY_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    // SE ELIMINARIA CUANDO SE TENGA QA ESTABLE API KEY
    // 'Api-key': process.env.REQUEST_HEADER_API_KEY,
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

/* REQUESTS & PARAMS */

export interface ConfigurationComponentRequest {
  configurationComponentId: number;
  productId: number;
  shipmentId: number;
  clientId: number;
}

export interface RegistryShipmentRequest {
  destinationResponsibleId: number;
  shipmentPriorityId: number;
  cost: number;
  currencyId: number;
  configurationComponents: {
    productId: number;
    clientId: number;
  }[];
}

export interface RemoveShipmentParams {
  shipmentId: number;
}

export interface ConfirmShipmentRequest {
  shipmentId: number;
  guideNumber: string;
  courierId: number;
  shippingDate: Date;
}

export interface CancelShipmentRequest {
  shipmentId: number;
}

export interface RemoveConfigurationComponentRequest {
  configurationComponentId: number;
}

export interface DisableConfigurationComponentRequest {
  configurationComponentId: number;
}

export interface ChangeAgreementAcceptedRequest {
  configurationComponentId: number;
  agreementAccepted: boolean;
}

export interface ModifyShipmentRequest {
  shipmentId: number;
  configurationComponentId: number;
  guideNumber: number;
  cost: number;
  currencyId: number;
  shippingDate: Date;
  destinationResponsibleId: number;
  shipmentPriorityId: number;
  shipmentStateId: number;
  configurationComponents: ConfigurationComponentRequest[];
}

export interface GetShippmentParams {
  shipmentId: number;
}

export interface ReceiptShipmentRequest {
  shipmentId?: number | string;
  configurationComponentId?: number;
  guideNumber: string | number;
  cost?: number;
  currencyId?: number;
  shippingDate: Date;
  destinationResponsibleId?: number;
  shipmentPriorityId?: number;
  shipmentStateId?: number;
  courierId: number;
  configurationComponents?: ConfigurationComponentRequest[];
}

export interface ShipmentsRequest {
  shipmentId?: number[];
  originType?: number[];
  destinationType?: number[];
  courierId?: number[];
  stateId?: number[];
  shipmentDateFrom?: Date | string;
  shipmentDateTo?: Date | string;
  receivedDateFrom?: Date | string;
  receivedDateTo?: Date | string;
  confirmedDate?: Date | string;
  receivedDate?: Date | string;
  orderColumn?: string;
  descending?: boolean;
  pageNumber?: number;
  pageSize?: number;
  text?: string | null;
  isExport?: boolean;
}

export interface InventoryShipmentRequest {
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  orderColumn?: string;
  descending?: boolean;
  isExport?: boolean;
  shipmentId?: number[];
  responsibleId?: number[];
}

export interface InventoryShipment {
  shipmentId: number;
  shipmentStateId: number;
  shipmentState: string;
  shippingDate: string;
  originResponsibleId: number;
  originResponsibleName: string;
  originResponsibleNameFullName: string;
  originResponsibleTypeId: number;
  originResponsibleType: string;
  destinationResponsibleId: number;
  destinyTypeId: number;
  priorityId: number;
  cost: number;
  currencyId: number;
  courierId: number;
  courierName: string;
  guideNumber: string;
  configurationComponents: ConfigurationComponentRequest[];
  createdDate: string;
  updatedDateTime: string;
  confirmedDate: string;
  receivedDate: string;
  createdUserId: number;
  destinationUserId: number;
  createdUserName: string;
  destinationResponsibleName: string;
  destinationResponsibleFullName: string;
  destinationResponsibleTypeId: number;
  destinationResponsibleType: string;
  message: string;
  canAcceptShipment: boolean;
  shipmentItemsCount: number;
  productsCount: number;
  kitsCount: number;
  suppliesCount: number;
  today?: string;
}

/* RESPONSE */

export interface SupplyResponse {
  suppyId: number;
  suppyStockId: number;
  supplyCode: string;
  name: string;
  description: string;
  unitMeasurementDescription: string;
  stockQuantity: number;
  quantityApply: number;
  lotNumber: string;
  usedQuantity: number;
}

export interface AccesoryResponse {
  accessoryModelStockId: number;
  accessoryModelId: number;
  serialNumber: string;
  respponsibleId: number;
  stockQuantity: number;
  accessoryName: string;
  accessoryDescription: string;
}

export interface ConfigurationComponentItem {
  description: string;
  productId: number;
  serialNumber: string;
  brandId: number;
  brandName: string;
  model: null | string;
  client: string;
  clientId: number;
  productStateId: number;
  state: string;
  stateCondition: string;
  stockNumber: null | number;
  agreementAccepted?: null | boolean;
  rejectedReasonId?: null | number;
  shipmentStatus?: boolean;
  configurationComponentId?: number;
  clientName?: string;
}

export interface ShipmentStatusTracking {
  id: number;
  shipmentId: number;
  initialStatus: string;
  finalStatus: string;
  createdDateTime: string;
  user: string;
  comment: string;
}

export interface ShipmentConfigurationComponentItem
  extends ConfigurationComponentItem {
  configurationComponentId: number;
  shipmentId: number;
}

export interface ShipmentResponse {
  shipmentId?: number;
  shipmentStateId?: number;
  shipmentStateName?: string;
  shipmentType?: string | null;
  guideNumber?: string;
  shippingDate?: Date | null;
  courierId?: number;
  courierName?: string;
  originResponsibleId?: number;
  originResponsibleName?: string;
  originResponsibleTypeId?: number;
  originResponsibleTypeName?: string;
  cost?: number;
  currencyId?: number;
  currencyName?: string;
  shipmentPriorityId?: number;
  shipmentPriorityName?: string;
  destinationResponsibleName?: string;
  destinationResponsibleId?: number;
  destinationResponsibleTypeId?: number;
  destinationResponsibleTypeName?: string;
  createdUserFullName?: string;
  createdUserId?: number;
  createdDate?: Date;
  enabled?: boolean;
  supplies?: Supply[];
  products?: Product[];
  kits?: Kit[];
}

export interface Kit {
  kitId?: number;
  kitName?: string;
  client?: string;
  products?: Product[];
  kitCreatedAt?: Date;
  kitCreatedBy?: string;
  responsible?: string;
  kitAgreementAccepted: null | boolean;
}

export interface Product {
  productBrand: string;
  productCondition: string;
  productId: number;
  productModel: string;
  productModelDescription: string;
  productStatus: string;
  serialNumber: string;
  shipmentItemId: number;
  productAgreementAccepted: null | boolean;
  rejectComment: string | null;
}

export interface TaskForceItem {
  email: string;
  firstLastName: string;
  firstName: string;
  fullName: string;
  responsibleId: number;
  secondLastName: string;
  userId: number;
}

export interface WarehouseItem {
  name: string;
  warehouseName: string;
  partnerId: number;
  responsibleId: number;
  warehouseId: number;
  warehouseResponsibleId: number;
  warehouseTypeId: number;
}

export interface RegistryShipmentResponse {
  shipmentId: number;
  courierId: number | null;
  shipmentStateId: number;
  shipmentState: string;
  originResponsibleId: number;
  destinationResponsibleId: number;
  createdDate: string;
  message: string | null;
}
export interface ResponsibleIdItem {
  responsibleId: number;
  warehousesResponsibleDescription?: string;
}

export interface ShipmentLoogBookConfigComponent {
  shipmentId: number;
  originName: string;
  destinationName: string;
  loadDate: Date;
  sentDate: Date;
  shipmentStatus: string;
}

export interface GetConfigurationComponentsShipmentTrackingPayload {
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  descending?: boolean;
  isExport?: boolean;
  productId?: number;
}

export interface DestinationEnginner {
  destinationId: number;
  responsibleId: number;
  destinationName: string;
}

export interface DestinationWarehouse {
  destinationId: number;
  responsibleId: number;
  destinationName: string;
}

export interface SuppliesResponse {
  id?: number;
  name?: string;
}

export interface SuppliesRequest {
  supplyId: number;
  responsibleId: number;
  quantity: number;
  inventoryPosition: number;
}

export interface EditSuppliesRequest {
  supplyStockId: number;
  quantity: number;
}

export interface SuppliesRequestForGet {
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  responsibleId?: number;
  unitMeasurementIds?: number[];
  supplyId?: number;
  isExport?: boolean;
  orderColumn?: string;
  descending?: boolean;
}

export interface SuppliesResponseForGet {
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  totalCount?: number;
  hasPrevious?: boolean;
  hasNext?: boolean;
  items?: ItemSuppliesResponseForGet[];
}

export interface SuppliesResponseForGetNoPagination {
  supplyStockId?: number;
  supplyId?: number;
  supplyCode?: string;
  responsible?: string;
  name?: string;
  unitMeasurement?: string;
  quantity?: number;
  cost?: number;
  currency?: string;
  responsibleId?: number;
  inventoryPosition?: number;
}

export interface ItemSuppliesResponseForGet {
  supplyStockId?: number;
  supplyId?: number;
  supplyCode?: string;
  responsible?: string;
  supplyName?: string;
  unitMeasurement?: string;
  quantity?: number;
  cost?: number;
  currency?: string;
}

export interface WarehouseSearchParams {
  warehouseId?: number[];
  warehouseAdminUserId?: number[];
  warehouseTypeId?: number[];
  userGroupId?: number[];
  description?: string;
  isLab?: boolean;
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  orderColumn?: string;
  descending?: boolean;
  enabled?: boolean | number;
  search?: string;
  status?: boolean | number;
  partnerId?: number[];
  isExport?: boolean;
}

export interface WarehousePostRequest {
  partnerId?: number;
  warehouseAdminUserId?: number;
  warehouseTypeId?: number;
  userGroupId?: number;
  description?: string;
  isLab?: boolean;
  canSendToLab?: boolean;
  lat?: number;
  long?: number;
  isExport?: boolean;
}

export interface WarehousePutRequest extends WarehousePostRequest {
  warehouseId: number;
  enabled: boolean;
}

export interface WarehouseSearched {
  ticketId?: number;
  address: WarehouseAddress;
  description: string;
  enabled: boolean;
  isDeleted: boolean;
  isLab: boolean;
  partnerId: number;
  userGroupId: number;
  warehouseAdminUserId: number;
  warehouseAdminUserName: string;
  warehouseId: number;
  warehouseTypeId: number;
  warehouseTypeName: string;
  canSendToLab?: boolean;
  partnerName?: string;
}

export interface WarehouseType {
  id: number;
  parnerId: number;
  description: string;
  enabled: boolean;
}

export interface WarehouseAddress {
  warehouseId: number;
  postalCodeId: number;
  postalCode?: string;
  municipalityId?: number;
  countryStateId?: number;
  localityId: number;
  streetName: string;
  streetNumber: string;
  internalNumber: string;
  lat: number;
  long: number;
  enabled: boolean;
}

export interface WarehousesInventory {
  productId?: number;
  client?: string;
  responsible?: string;
  responsibleId: number;
  serialNumber?: string;
  productBrand?: string;
  productModel?: string;
  productModelDescription?: string;
  productStatus?: string;
  productCondition?: string;
  kitId?: number;
  kitName?: string;
  stockNumber?: number;
  warehouseExit?: Date;
  simNumber?: number;
  imeiNumber?: number;
  ipaddress?: number;
  portNumber?: number;
  comment?: string;
  isClientProperty?: boolean;
  canSendToLab?: boolean;
  updatedDateTime?: Date;
  sku?: string;
  movement?: string;
  laboratoryTechnician?: string;
  productStatusId: number;
  attentionTime: AttentionTime;
  productModelId?: number;
}

interface AttentionTime {
  days: number;
  hours: number;
  minutes: number;
}

export interface GetLaboratoryComponentInventoryRequest {
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  orderColumn?: string;
  descending?: boolean;
  isExport?: boolean;
  clientId?: number;
  responsibleId?: number;
  active?: boolean | number;
  productStatusIds?: number[];
  productConditionIds?: number[];
  productBrandId?: number;
  productModelId?: number;
  isLab?: boolean;
  serialNumber?: string;
  laboratoryTechnicianId?: number | number[];
  allowSelfAssignment?: boolean;
}

export interface GetWarehousesComponentInventoryPayload {
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  orderColumn?: string;
  descending?: boolean;
  isExport?: boolean;
  clientId?: number;
  responsibleId?: number;
  active?: boolean | number;
  isLab?: boolean;
  productStatusIds?: number[];
  productConditionIds?: number[];
  serialNumber?: string;
  allowSelfAssignment?: boolean;
}

export interface WarehousesKit {
  kitId?: number;
  kitCIName?: string;
  kitCIStatus?: string;
  cantProduct?: number;
  client?: string;
  responsible?: string;
  enabled?: boolean;
}

export interface Supply {
  supplyId: number;
  supplyName: string;
  unitMeasurement: string;
  supplyUnitMeasurement?: string;
  cost: string;
  responsible: string;
  quantity: string;
  shipmentItemId: number;
  supplyAgreementAccepted: null | boolean;
  supplyStockId?: number;
  createdDateTime?: Date;
  createdUserId?: number;
  createdUserName?: string;
}

export interface WorkForcesComponentInventoryRequest {
  responsibleId?: number;
  clientId?: number;
  orderColumn?: string;
  descending?: boolean;
  pageNumber?: number;
  pageSize?: number;
  text?: string | null;
  productStatusIds?: number[];
  productConditionIds?: number[];
  active?: boolean | number;
  isExport?: boolean;
  productBrandId?: number;
  productModelId?: number;
}
export interface WorkForcesComponentInventoryResponse {
  client: string;
  productBrand: string;
  productCondition: string;
  productId: number;
  productModel: string;
  productModelDescription: string;
  productStatus: string;
  responsible: string;
  serialNumber: string;
  productBrandName?: string;
  productModelName?: string;
  clientName?: string;
}

export interface WorkForcesSuppliesInventoryRequest {
  responsibleId?: number;
  unitMeasurement?: number[];
  enabled?: number;
  supplyId?: number;
  orderColumn?: string;
  descending?: boolean;
  pageNumber?: number;
  pageSize?: number;
  text?: string | null;
  isExport?: boolean;
}

export interface WorkForcesSuppliesInventoryResponse {
  responsible: string;
  cost: string;
  quantity: string;
  supplyId: number;
  supplyName: string;
  unitMeasurement: string;
  supplyStockId: number;
}

export interface ShippingSuppliesResponse {
  id?: number;
  name?: string;
  unitMeasurementName?: string;
  qty?: string;
  addDate?: string;
  addBy?: string;
}

export interface WorkForcesKitsInventoryRequest {
  responsibleId?: number;
  active?: boolean | number;
  clientId?: number;
  kitStatusIds?: number[];
  pageNumber?: number;
  pageSize?: number;
  text?: string | null;
  orderColumn?: string;
  descending?: boolean;
  serviceId?: number;
  isExport?: boolean;
}

export interface LocationsComponentInventoryRequest
  extends WorkForcesKitsInventoryRequest {
  locationId?: number;
}

export interface WorkForcesKitsInventoryResponse {
  kitId: number;
  kitCIName: string;
  kitCIStatus: string;
  cantProduct: number;
  client: string;
  responsible: string;
  enabled: boolean | string;
}

export interface UpdateDestinationWarehousesRequest {
  warehouseId: number;
  destinationsIds: number[];
}

export interface UpdateDestinationEginnersRequest {
  warehouseId: number;
  destinationsIds: number[];
}

export interface WorkForcesKITSInventoryParams {
  ResponsibleId?: number;
  active?: boolean | number;
  clientId?: number;
  KitStatusIds?: number[];
  PageNumber?: number;
  PageSize?: number;
  Text?: string | null;
  OrderColumn?: string;
  Descending?: boolean;
  IsExport?: boolean;
}

export interface GetShipmetResponse {
  shipmentId: number;
  shipmentStateId: number;
  shipmentStateName: string;
  shipmentType: string;
  guideNumber: string;
  shippingDate: Date;
  courierId: number;
  courierName: string;
  originResponsibleId: number;
  originResponsibleName: string;
  originResponsibleTypeId: number;
  originResponsibleTypeName: string;
  cost: number;
  currencyId: number;
  currencyName: string;
  shipmentPriorityId: number;
  shipmentPriorityName: string;
  destinationResponsibleName: string;
  destinationResponsibleId: number;
  destinationResponsibleTypeId: number;
  destinationResponsibleTypeName: string;
  canAcceptShipment: string;
  originResponsibleType: string;
  destinationResponsibleType: string;
  createdDate: string;
  createdUserId: number;
  createdUserFullName: string;
  enabled: boolean;
  receivedDateTime: string;
  supplies: Supply[];
  products: Product[];
  kits: Kit[];
}

export interface CreateShipmentRequest {
  originResponsibleId?: number;
  destinationResponsibleId?: number;
  cost?: number;
  currencyId?: number;
  guideNumber?: string;
  courierId?: number;
  shipmentPriorityId?: number;
  productIds?: number[];
  supplies?: CreateShipmentSupplyRequest[];
  kitIds?: number[];
  shipmentType?: string;
}

export interface CreateShipmentSupplyRequest {
  supplyStockId?: number;
  quantity?: number;
}

export interface ShipmentEditRequest {
  shipmentId?: number;
  supplies?: EditSupplyRequest[];
  kitIds?: number[];
  productIds?: number[];
}

export interface ShipmentStatusTrackingParams {
  shipmentId?: number;
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  orderColumn?: string;
  descending?: boolean;
  isExport?: boolean;
}

export interface EditSupplyRequest {
  supplyStockId?: number;
  quantity?: number;
}

export type ReceptionShipmentItemsPayload = {
  shipmentId: number;
  acceptedItemIds?: number[];
  rejectedItems?: { id: number; comment: string }[];
};

export type Audit = {
  name: SetStateAction<string>;
  id: number;
  auditId: number;
  auditName?: string;
  auditTypeId: number;
  auditTypeName: string;
  description: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  enabled: boolean;
  default?: boolean;
};

export type FormCustom = {
  id: number;
  auditId: number;
  auditName?: string;
  auditTypeId: number;
  auditTypeName: string;
  description: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  enabled: boolean;
  default?: boolean;
};

export type AuditReport = {
  auditReportId: number;
  auditTypeId: number;
  auditTypeName: string;
  description: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  enabled: boolean;
  assignedEnginnerName: string;
  assignedEnginnerId: number;
  auditCreatedUserId: number;
  auditCreatedUserName: string;
  fileDescriptorId?: string;
  fileDescriptorName?: string;
  productBrandName?: string;
  productBrandId?: string;
  effectiveness: number;
  fileDescriptorUrlPath?: string;
};

export type NewAuditPayload = {
  auditTypeId: number;
  description: string;
  startDate: string;
  endDate: string;
};

export type GeneralSearchAuditPayload = {
  pageNumber: number;
  pageSize: number;
  text: string;
  orderColumn: string;
  descending: boolean;
  isExport: boolean;
  auditId: number[];
  auditTypeId: number[];
  description: string;
  enabled: boolean;
  fromDate: string;
  toDate: string;
};

export type GeneralSearchAuditReport = {
  pageNumber: number;
  pageSize: number;
  text: string;
  orderColumn: string;
  descending: boolean;
  isExport: boolean;
  auditId: number[];
  productBrandId: number[];
  fromDate: string;
  toDate: string;
};

export interface AuditReportRequest {
  auditId: number;
  productSerialNumbers: string[];
}

export interface AuditReportData {
  auditReportId: number;
  auditId: number;
  auditName: string;
  auditTypeId: number;
  auditTypeName: string;
  assignedEnginnerId: number;
  assignedEnginnerName: string;
  assignedEnginnerUserName: string;
  reportedItems: number;
  registeredItems: number;
  coincidentItems: number;
  notCoincidentItems: number;
  inTransitItems: number;
  nonExistentItems: number;
  effectiveness: number;
  registeredProducts: RegisteredProduct[];
  inTransitProducts: InTransitProduct[];
  coincidentProducts: CoincidentProduct[];
  notCoincidentProducts: NotCoincidentProduct[];
  nonExistentProducts: string[];
  createdDateTimeUTC: string;
  reportDate: string;
}

export interface RegisteredProduct {
  productId: number;
  clientId: number;
  clientName: string;
  serialNumber: string;
  productBrandId: number;
  productBrandName: string;
  productModelId: number;
  productModelName: string;
  responsibleId: number;
  productStatusId: number;
}

export interface InTransitProduct {
  productId: number;
  clientId: number;
  clientName: string;
  serialNumber: string;
  productBrandId: number;
  productBrandName: string;
  productModelId: number;
  productModelName: string;
  responsibleId: number;
  productStatusId: number;
}

export interface CoincidentProduct {
  productId: number;
  clientId: number;
  clientName: string;
  serialNumber: string;
  productBrandId: number;
  productBrandName: string;
  productModelId: number;
  productModelName: string;
  responsibleId: number;
  productStatusId: number;
}

export interface NotCoincidentProduct {
  productId: number;
  clientId: number;
  clientName: string;
  serialNumber: string;
  productBrandId: number;
  productBrandName: string;
  productModelId: number;
  productModelName: string;
  responsibleId: number;
  productStatusId: number;
}

export interface ValidateShipmentComponentsRequest {
  originResponsibleId: number;
  destinationResponsibleId: number;
  serialNumbers: {
    serialNumber: string;
  }[];
}

export interface ValidateShipmentComponentsResponse {
  createdDateTimeUTC: string;
  errorSerialNumbers: {
    errorMsg: string;
    serialNumber: string;
  }[];
  validSerialNumbers: {
    createdDate: string;
    serialNumber: string;
  }[];
}

export interface IValidProducts {
  client: string;
  productBrand: string;
  productId: number;
  productModel: string;
  serialNumber: string;
}
export interface ILabCIToBulkUpload {
  invalidProducts: { serialNumber: string; errorMsg?: string }[];
  validProducts: IValidProducts[];
}

export default {
  instance,
  taskForce: {
    /**
     * No description
     *
     * @tags TaskForce
     * @name getGeneralSupplies
     * @request GET:/api/v1/TaskForce/me/Supplies
     * @secure
     */
    getGeneralSupplies: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SupplyResponse[]>>({
        url: `/TaskForce/me/Supplies`,
        method: 'GET',
        ...config
      }),
    /**
     * TODOS LOS ACCESORIOS DEL EL USUARIO LOGUEADO CON RELACION AL CLIENTE
     *
     * @tags TaskForce
     * @name getGeneralAccesories
     * @request GET:/api/v1/TaskForce/me/Accessories
     * @secure
     */
    getGeneralAccesories: (
      clientId?: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AccesoryResponse[]>>({
        url: `/TaskForce/me/Accessories?clientId=${clientId}`,
        method: 'GET',
        ...config
      }),
    /**
     * TODOS LOS PRODUCTOS DEL PRODUCTO
     *
     * @tags TaskForce
     * @name getMeAccesoriesByProductID
     * @request GET:/api/v1/TaskForce/me/AccessoriesByProduct
     * @secure
     */
    getMeAccesoriesByProductID: (
      params: { productId: number; locationId?: number; clientId?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AccesoryResponse[]>>({
        url: `/TaskForce/me/AccessoriesByProduct`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TODOS LOS ACCESORIOS DE UNA UBICACION
     *
     * @tags TaskForce
     * @name getMeAccesoriesTotalByLocId
     * @request GET: /inventory/api/v1/TaskForce/me/AccessoriesByLocation
     * @secure
     */
    getMeAccesoriesTotalByLocId: (
      locationId: number,
      clientId?: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AccesoryResponse[]>>({
        url: `/TaskForce/me/AccessoriesByLocation?locationId=${locationId}&clientId=${clientId}`,
        method: 'GET',
        ...config
      }),
    /**
     * No description
     *
     * @tags TaskForce
     * @name getMeSuppliesByID
     * @request GET:/api/v1/TaskForce/me/SuppliesByTicketId
     * @secure
     */
    getMeSuppliesByID: (
      params: { ticketId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<SupplyResponse[]>>({
        url: `/TaskForce/me/SuppliesByTicketId`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * No description
     *
     * @tags TaskForce
     * @name getMeAccesoriesByID
     * @request GET:/api/v1/TaskForce/me/AccessoriesModelByTicketId
     * @secure
     */
    getMeAccesoriesByID: (
      params: { ticketId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AccesoryResponse[]>>({
        url: `/TaskForce/me/AccessoriesModelByTicketId`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Obtiene los posibles origenes de un envio cuando el tipo de destino es Ingeniero
     *
     * @tags TaskForce
     * @name GetOriginTaskForcesByUser
     * @request GET:/api/v1/TaskForce/GetOriginTaskForcesByUser
     * @secure
     */
    GetOriginTaskForcesByUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TaskForceItem[]>>({
        url: `/TaskForce/GetOriginTaskForcesByUser`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene los posibles envios de un envio cuando el tipo de destino es Ingeniero
     *
     * @tags TaskForce
     * @name GetOriginTaskForcesByUser
     * @request GET:/api/v1/TaskForce/GetDestinationTaskForcesByUser
     * @secure
     */
    GetDestinationTaskForcesByUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TaskForceItem[]>>({
        url: `/TaskForce/GetDestinationTaskForcesByUser`,
        method: 'GET',
        ...config
      })
  },
  warehouse: {
    /**
     * Obtiene los posibles origenes del almacenes
     *
     * @tags warehouse
     * @name GetDestinyWarehouses
     * @request GET:/api/v1/Warehouse/me/GetDestinyWarehouses
     * @secure
     */
    GetDestinyWarehouses: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<WarehouseItem[]>>({
        url: `/Warehouse/me/GetDestinyWarehouses`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los almacenes de un usuario
     *
     * @tags warehouse
     * @name GetDestinyWarehousesByUserId
     * @request GET:/api/v1/Warehouse/GetDestinyWarehouses
     * @secure
     */
    GetDestinyWarehousesByUserId: (
      originTaskForceId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<WarehouseItem[]>>({
        url: `/Warehouse/GetDestinyWarehouses?originTaskForceId=${originTaskForceId}&forScheduled=true`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los posibles origenes del almacenes
     *
     * @tags warehouse
     * @name GetOriginWarehouseByUser
     * @request GET:/api/v1/Warehouse/GetOriginWarehouseByUser
     * @secure
     */
    GetOriginWarehouseByUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<WarehouseItem[]>>({
        url: `/Warehouse/GetOriginWarehouseByUser`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los posibles destinos del almacenes
     *
     * @tags warehouse
     * @name GetDestinyWarehouseByUser
     * @request GET:/api/v1/Warehouse/GetDestinyWarehouseByUser
     * @secure
     */
    GetDestinyWarehouseByUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<WarehouseItem[]>>({
        url: `/Warehouse/GetDestinyWarehouseByUser`,
        method: 'GET',
        ...config
      }),

    /**
     * origen de envio es almacen para ingeniero
     *
     * @tags warehouse
     * @name get
     * @request GET:/api/v1/Warehouse/GetDestinationEnginners/{warehouseId}
     */
    GetDestinationEnginnersById: (
      warehouseId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/GetDestinyWarehouses?originTaskForceId=${warehouseId}`,
        method: 'GET',
        ...config
      }),

    /**
     * origen de envio es almacen para almacen
     *
     * @tags warehouse
     * @name get
     * @request GET:/api/v1/Warehouse/GetDestinationWarehouses/{warehouseId}
     */
    GetDestinationWarehousesById: (
      warehouseId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<DestinationWarehouse[]>>({
        url: `/Warehouse/GetDestinationWarehouses/${warehouseId}`,
        method: 'GET',
        ...config
      }),

    /**
     * origen de envio es fuerza de trabajo para una fuerza de trabajo
     *
     * @tags GetDestinyTaskForces
     * @name get
     * @request GET:/api/v1/TaskForce/GetDestinyTaskForces?originTaskForceId={forcesId}
     */
    GetDestinyTaskForcesById: (
      forcesId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/TaskForce/GetEnabledTaskForces`,
        method: 'GET',
        ...config
      }),

    /**
     * origen de envio es fuerza de trabo para un almacen
     *
     * @tags GetDestinyWarehouses
     * @name get
     * @request GET:/api/v1/Warehouse/GetDestinyWarehouses?originTaskForceId={forcesId}
     */
    GetDestinyWarehousesById: (
      forcesId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/GetDestinationEnginners/${forcesId}`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los almacenes del que un Jefe de almacen es responsable
     *
     * @tags warehouse
     * @name getResponsibleIdByUser
     * @request GET:api/v1/Warehouse/me/GetResponsibleIdByUser
     * @secure
     */
    getResponsibleIdByUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ResponsibleIdItem>>({
        url: `/Warehouse/me/GetResponsibleIdByUser`,
        method: 'GET',
        ...config
      }),

    /**
     * Warehouse GeneralSearch
     * @tags warehouse
     * @name generalSearch
     * @request POST:/api/v1/Warehouse/GeneralSearch
     * @secure
     */
    generalSearch: (
      data: WarehouseSearchParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WarehouseSearched>>
      >({
        url: `/Warehouse/GeneralSearch`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * warehouse add
     * @tags warehouse
     * @name add
     * @request POST:/api/v1/Warehouse
     * @secure
     */
    add: (data: WarehousePostRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * warehouse update
     * @tags warehouse
     * @name update
     * @request PUT:/api/v1/Warehouse
     * @secure
     */
    update: (data: WarehousePutRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * warehouse delete
     * @tags warehouse
     * @name delete
     * @request DELETE:/api/v1/Warehouse/{warehouseId}
     */
    delete: (warehouseId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/${warehouseId}`,
        method: 'DELETE',
        ...config
      }),

    /**
     * warehouse get
     * @tags warehouse
     * @name get
     * @request GET:/api/v1/Warehouse/{warehouseId}
     */
    get: (warehouseId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/${warehouseId}`,
        method: 'GET',
        ...config
      }),

    /**
     * warehouse createWarehouseAdress
     * @tags warehouse
     * @name createWarehouseAdress
     * @request POST:/api/v1/Warehouse/CreateWarehouseAdress
     * @secure
     */
    createWarehouseAddress: (
      data: WarehouseAddress,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/CreateWarehouseAddress`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * warehouse updateWarehouseAdress
     * @tags warehouse
     * @name updateWarehouseAdress
     * @request PUT:/api/v1/Warehouse/UpdateWarehouseAdress
     * @secure
     */
    updateWarehouseAddress: (
      data: WarehouseAddress,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/UpdateWarehouseAddress`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Obtiene el inventario de responsables tipo almacén
     *
     * @tags warehouse
     * @name getWarehousesComponentInventory
     * @request POST/api/v1/Inventories/GetWarehousesComponentInventory
     * @secure
     */
    getWarehousesComponentInventory: (
      data: GetWarehousesComponentInventoryPayload,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WarehousesInventory>>
      >({
        url: `/Inventories/GetWarehousesComponentInventory`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * @tags laboratory
     * @name getLaboratoryComponentInventory
     * @request POST:/api/v1/Inventories/GetLaboratoryComponentInventory
     * @secure
     */
    getLaboratoryComponentInventory: (
      data: GetLaboratoryComponentInventoryRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WarehousesInventory>>
      >({
        url: `/Inventories/GetLaboratoryComponentInventory`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtiene el inventario de KITS de  tipo almacén
     *
     * @tags warehouse
     * @name getWarehousesKitsInventory
     * @request POST:/api/v1/Inventories/GetWarehousesKitsInventory
     * @secure
     */
    getWarehousesKitsInventory: (
      params: WorkForcesKITSInventoryParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<WarehousesKit>>>({
        url: `/Inventories/GetWarehousesKitsInventory`,
        method: 'POST',
        params,
        paramsSerializer: () =>
          qs.stringify(params, {
            arrayFormat: 'repeat'
          }),
        ...config
      }),

    /**
     * Actualiza los almacenes de destino
     * @tags warehouse
     * @name updateDestinationWarehouses
     * @request POST:/api/v1/Warehouse/UpdateDestinationWarehouses
     * @secure
     */
    updateDestinationWarehouses: (
      data: UpdateDestinationWarehousesRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/UpdateDestinationWarehouses`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Actualiza los ingenieros de destino
     * @tags warehouse
     * @name updateDestinationEginners
     * @request POST:/api/v1/Warehouse/UpdateDestinationEnginners
     * @secure
     */
    updateDestinationEginners: (
      data: UpdateDestinationEginnersRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Warehouse/UpdateDestinationEnginners`,
        method: 'POST',
        data,
        ...config
      }),

    /** Obtiene los ingenieros de destino
     * @tags warehouse
     * @name getDestinationEnginners
     * @request GET:/api/v1/Warehouse/GetDestinationEnginners/{warehouseId}
     * @secure
     */
    getDestinationEnginners: (
      warehouseId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<DestinationEnginner[]>>({
        url: `/Warehouse/GetDestinationEnginners/${warehouseId}`,
        method: 'GET',
        ...config
      }),

    /** Obtiene los ingenieros de destino
     * @tags warehouse
     * @name getDestinationWarehouses
     * @request GET:/api/v1/Warehouse/GetDestinationWarehouses/{warehouseId}
     * @secure
     */
    getDestinationWarehouses: (
      warehouseId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<DestinationWarehouse[]>>({
        url: `/Warehouse/GetDestinationWarehouses/${warehouseId}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene el inventario de insumos de responsables tipo almacén
     *
     * @tags warehouse
     * @name getWarehousesSupplyInventory
     * @request POST/api/v1/Inventories/GetWarehousesSupplyInventory
     * @secure
     */
    getWarehousesSupplyInventory: (
      data: any,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<Supply>>>({
        url: `/Inventories/GetWarehousesSupplyInventory`,
        method: 'POST',
        data,
        ...config
      })
  },
  warehouseTypes: {
    /**
     * warehouseTypes get
     * @tags warehouseTypes
     * @name get
     * @request GET:/api/v1/WarehousesTypes
     * @secure
     */
    get: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<WarehouseType[]>>({
        url: `/WarehousesTypes`,
        method: 'GET',
        ...config
      })
  },
  shipment: {
    /**
     * Registra el envío
     *
     * @tags shipment
     * @name registryShipment
     * @request POST:/api/v1/Shipment/CreateShipment
     * @secure
     */
    CreateShipment: (
      data: CreateShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<RegistryShipmentResponse>>({
        url: `/Shipment/CreateShipment`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Registra el envío
     *
     * @tags shipment
     * @name registryShipment
     * @request POST:/api/v1/Shipment/me/RegistryShipment
     * @secure
     */
    registryShipment: (
      data: RegistryShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<RegistryShipmentResponse>>({
        url: `/Shipment/me/RegistryShipment`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina el envío
     *
     * @tags shipment
     * @name removeShipment
     * @request DELETE:/api/v1/Shipment/me/RemoveShipment
     * @secure
     */
    removeShipment: (
      params: RemoveShipmentParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/RemoveShipment`,
        method: 'DELETE',
        params,
        ...config
      }),
    /**
     * Confirma el envío
     *
     * @tags shipment
     * @name confirmRegisterShipment
     * @request PUT:/api/v1/Shipment/me/ConfirmRegisterShipment
     * @secure
     */
    confirmRegisterShipment: (
      data: ConfirmShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/ConfirmRegisterShipment`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Modifica el envío
     *
     * @tags shipment
     * @name modifyRegisterShipment
     * @request PUT:/api/v1/Shipment/me/ModifyRegisterShipment
     * @secure
     */
    modifyRegisterShipment: (
      data: ConfirmShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/ModifyRegisterShipment`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Cancela el envío
     *
     * @tags shipment
     * @name cancelShipment
     * @request PUT:/api/v1/Shipment/me/CancelShipment
     * @secure
     */
    cancelShipment: (
      data: CancelShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/CancelShipment`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Obtiene datos de un envío
     *
     * @tags shipment
     * @name getShipmentById
     * @request GET: /api/v1/Shipment/1
     * @secure
     */
    getShipmentById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ShipmentResponse>>({
        url: `/Shipment/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Marca el envío como "Recibido"
     *
     * @tags shipment
     * @name receiptShipment
     * @request POST:/api/v1/Shipment/me/ReceiptShipment
     * @secure
     */
    receiptShipment: (
      data: ReceiptShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/ReceiptShipment`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * @tags shipment
     * @name getShipments
     * @request POST:/api/v1/Shipment/me/GetShipments
     * @secure
     */
    getShipments: (data: ShipmentsRequest, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<GetShipmetResponse>>
      >({
        url: `/Shipment/me/GetShipments`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * @tags shipment
     * @name getShipments
     * @request POST:/api/v1/Shipment/me/GetInventoryShipments
     * @secure
     */
    getInventoryShipments: (
      data: InventoryShipmentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<InventoryShipment>>
      >({
        url: `/Shipment/me/GetInventoryShipments`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * @tags shipment
     * @name getPendingShipmentsByUser
     * @request GET:/api/v1/Shipment/me/GetPendingShipmentsByUser
     * @secure
     */
    getPendingShipmentsByUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/GetPendingShipmentsByUser`,
        method: 'GET',
        ...config
      }),

    /**
     * @tags shipment
     * @name setReadyToShip
     * @request PUT:/api/v1/Shipment/me/SetReadyToShip
     * @secure
     */
    setReadyToShip: (shipmentId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/SetReadyToShip`,
        method: 'PUT',
        params: { shipmentId },
        ...config
      }),

    /**
     * @tags shipment
     * @name setOnProcess
     * @request PUT:/api/v1/Shipment/me/SetOnProcess
     * @secure
     */
    setOnProcess: (shipmentId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/me/SetOnProcess`,
        method: 'PUT',
        params: { shipmentId },
        ...config
      }),

    /**
     * @tags shipment
     * @name EditShipment
     * @request POST:/api/v1/Shipment/UpdateShipment
     * @secure
     */
    EditShipment: (
      data: ShipmentEditRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Shipment/UpdateShipment`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtiene la bitácora de un envío
     *
     * @tags shipmentConfigurationComponent
     * @name ShipmentStatusTracking
     * @request GET: /api/v1/Shipment/ShipmentStatusTracking
     * @secure
     */
    ShipmentStatusTracking: (
      params: ShipmentStatusTrackingParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ShipmentStatusTracking[]>>({
        url: `/Shipment/ShipmentStatusTracking`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Obtiene un envío por su identificador único.
     * @description Obtiene un envío por su identificador único.
     * @tags shipmentConfigurationComponent
     * @name ShipmentStatusTracking
     * @request GET: /api/v1/Shipment/{id}
     * @secure
     */
    GetShipmentById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<GetShipmetResponse>>({
        url: `/Shipment/${id}`,
        method: 'GET',
        ...config
      }),

    /**
     * @tags Shipment
     * @name ShipmentByWorkForce
     * @request POST:/api/v1/Shipment/ShipmentByWorkForce
     * @secure
     */
    getShipmentByWorkForce: (
      workForceId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<
          {
            id: number;
            state: number;
          }[]
        >
      >({
        url: `/Shipment/ShipmentByWorkForce`,
        method: 'GET',
        params: {
          workForceId
        },
        ...config
      })
  },
  shipmentConfigurationComponent: {
    /**
     * Obtiene los posibles dispositivos a enviar por el usuario
     *
     * @tags shipmentConfigurationComponent
     * @name getConfigurationComponentsItemsByUser
     * @request GET:/api/v1/ShipmentConfigurationComponent/me/GetConfigurationComponentsItemsByUser
     * @secure
     */
    getConfigurationComponentsItemsByUser: (
      destinyUserId?: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ConfigurationComponentItem[]>
      >({
        url: `/ShipmentConfigurationComponent/me/GetConfigurationComponentsItemsByUser${
          destinyUserId ? `?destinyUserId=${destinyUserId}` : ''
        }`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los dispositivos asociados a un envio en particular
     *
     * @tags shipmentConfigurationComponent
     * @name getAllConfigurationComponentsByShipment
     * @request GET: /api/v1/ShipmentConfigurationComponent/me/GetAllConfigurationComponentsByShipment
     * @secure
     */
    getAllConfigurationComponentsByShipment: (
      params: { shipmentId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ShipmentConfigurationComponentItem[]>
      >({
        url: `/ShipmentConfigurationComponent/me/GetAllConfigurationComponentsByShipment`,
        method: 'GET',
        ...config,
        params
      }),
    /**
     * Añade un dispositivo al envío
     *
     * @tags shipmentConfigurationComponent
     * @name addConfigurationComponent
     * @request POST:/api/v1/ShipmentConfigurationComponent/me/AddConfigurationComponent
     * @secure
     */
    addConfigurationComponent: (
      data: ConfigurationComponentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ShipmentConfigurationComponent/me/AddConfigurationComponent`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Quita un dispositivo del envío
     *
     * @tags shipmentConfigurationComponent
     * @name removeConfigurationComponent
     * @request PUT:/api/v1/ShipmentConfigurationComponent/me/RemoveConfigurationComponent
     * @secure
     */
    removeConfigurationComponent: (
      data: RemoveConfigurationComponentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ShipmentConfigurationComponent/me/RemoveConfigurationComponent`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Deshabilita un dispositivo del envío
     *
     * @tags shipmentConfigurationComponent
     * @name disableConfigurationComponent
     * @request PUT:/api/v1/ShipmentConfigurationComponent/me/DisableConfigurationComponent
     * @secure
     */
    disableConfigurationComponent: (
      data: DisableConfigurationComponentRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ShipmentConfigurationComponent/me/DisableConfigurationComponent`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Marca el dispositivo como aceptado o rechazado
     *
     * @tags shipmentConfigurationComponent
     * @name changeAgreementAccepted
     * @request PUT:/api/v1/ShipmentConfigurationComponent/me/ChangeAgreementAccepted
     * @secure
     */
    changeAgreementAccepted: (
      data: ChangeAgreementAcceptedRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ShipmentConfigurationComponent/me/ChangeAgreementAccepted`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Obtiene los envíos asociados a un componente de configuración
     *
     * @tags ShipmentItems
     * @name getShipmentTracking
     * @request POST:/api/v1/ShipmentItems/GetConfigurationComponentsShipmentTracking
     * @secure
     */
    getConfigurationComponentsShipmentTracking: (
      payload: GetConfigurationComponentsShipmentTrackingPayload,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ShipmentLoogBookConfigComponent>>
      >({
        url: `/ShipmentItems/GetConfigurationComponentsShipmentTracking`,
        method: 'POST',
        data: payload,
        ...config
      })
  },
  supplies: {
    /**
     * Obtiene los posibles dispositivos a enviar por el usuario
     *
     * @tags shipmentConfigurationComponent
     * @name getAllSupplies
     * @request GET:/api/v1/Supply
     * @secure
     */
    getAllSupplies: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SuppliesResponse[]>>({
        url: `/Supply`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene los posibles dispositivos a enviar por el usuario
     *
     * @tags shipmentConfigurationComponent
     * @name createSupplyStock
     * @request POST:/api/v1/SupplyStock
     * @secure
     */
    createSupplyStock: (
      data: SuppliesRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/SupplyStock`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Edita las existencias de Insumos
     *
     * @tags shipmentConfigurationComponent
     * @name editSupplyStock
     * @request POST:/api/v1/SupplyStock/AddStock
     * @secure
     */
    editSupplyStock: (
      data: EditSuppliesRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/SupplyStock/AddStock`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene todos las existencias de Insumos paginadas
     *
     * @tags shipmentConfigurationComponent
     * @name getSupplyStock
     * @request POST:/api/v1/SupplyStock/GeneralSearch
     * @secure
     */
    getSupplyStock: (
      data: SuppliesRequestForGet,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<SuppliesResponseForGet>>
      >({
        url: `/SupplyStock/GeneralSearch`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene todos las existencias de Insumos sin paginar
     *
     * @tags shipmentConfigurationComponent
     * @name getAllSupplyStock
     * @request GET:/api/v1/SupplyStock?responsibleId=1
     * @secure
     */
    getAllSupplyStock: (data: string, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<SuppliesResponseForGetNoPagination>
      >({
        url: `/SupplyStock?${data}`,
        method: 'GET',
        ...config
      }),
    /**
     * Borra las existencias de Insumos por id
     *
     * @tags shipmentConfigurationComponent
     * @name deleteSupplyStock
     * @request DELETE:/api/v1/SupplyStock/3
     * @secure
     */
    deleteSupplyStock: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/SupplyStock/${id}`,
        method: 'DELETE',
        ...config
      })
  },
  inventories: {
    /**
     * @tags Inventories
     * @name getWorkForcesComponentInventory
     * @request POST:/api/v1/Inventories/GetWorkForcesComponentInventory
     * @secure
     */
    getWorkForcesComponentInventory: (
      data: WorkForcesComponentInventoryRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WorkForcesComponentInventoryResponse>>
      >({
        url: `/Inventories/GetWorkForcesComponentInventory`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * @tags Inventories
     * @name getWorkForcesSupplyInventory
     * @request POST:/api/v1/Inventories/GetWorkForcesSupplyInventory
     * @secure
     */
    getWorkForcesSupplyInventory: (
      data: WorkForcesSuppliesInventoryRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WorkForcesSuppliesInventoryResponse>>
      >({
        url: `/Inventories/GetWorkForcesSupplyInventory`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * @tags Inventories
     * @name GetWorkForcesKitsInventory
     * @request POST:/api/v1/Inventories/GetWorkForcesKitsInventory
     * @secure
     */
    getWorkForcesKitsInventory: (
      params?: WorkForcesKitsInventoryRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WorkForcesKitsInventoryResponse>>
      >({
        url: `/Inventories/GetWorkForcesKitsInventory`,
        method: 'POST',
        params,
        paramsSerializer: () =>
          qs.stringify(params, {
            arrayFormat: 'repeat'
          }),
        ...config
      }),
    /**
     * @tags Inventories
     * @name GetLocationsComponentInventory
     * @request POST:/api/v1/Inventories/GetLocationsComponentInventory'
     * @secure
     */
    getLocationsComponentInventory: (
      data: LocationsComponentInventoryRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WorkForcesComponentInventoryResponse>>
      >({
        url: `/Inventories/GetLocationsComponentInventory`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * @tags Inventories
     * @name getLaboratoryComponentsToBulkUpload
     * @request POST:/api/v1/Inventories/GetLaboratoryComponentsToBulkUpload
     * @secure
     */
    GetLaboratoryComponentsToBulkUpload: (
      data: { serialNumbers: string[] },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ILabCIToBulkUpload>>({
        url: `/Inventories/GetLaboratoryComponentsToBulkUpload`,
        method: 'POST',
        data,
        ...config
      })
  },
  shipmentItems: {
    /**
     * Confirma o rechaza la recepción parcial o total de un envío
     *
     * @tags shipmentConfigurationComponent
     * @name receptionShipmentItems
     * @request POST:/api/v1/ShipmentItems/ReceptionShipmentItems
     * @secure
     */
    receptionShipmentItems: (
      payload: ReceptionShipmentItemsPayload,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ShipmentItems/ReceptionShipmentItems`,
        method: 'POST',
        data: payload,
        ...config
      }),

    /**
     * Valida los componentes de carga masiva de un envio
     *
     * @tags shipmentItems
     * @name validateShipmentComponents
     * @request POST:/api/v1/ShipmentItems/ValidateShipmentComponents
     * @secure
     */

    validateShipmentComponents: (
      data: ValidateShipmentComponentsRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ValidateShipmentComponentsResponse>
      >({
        url: `/ShipmentItems/ValidateShipmentComponents`,
        method: 'POST',
        data,
        ...config
      })
  },
  audit: {
    /**
     * Busqueda general de Auditorias
     * @tags Supplies
     * @name GeneralSearch
     * @request POST: /api/v1/Audit/GeneralSearch
     * @secure
     */
    generalSearch: (
      data: GeneralSearchAuditPayload,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<Audit>>>({
        url: `/Audit/GeneralSearch`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Crea una Auditoria
     *
     * @tags shipment
     * @name registryShipment
     * @request POST:/api/v1/Audit
     * @secure
     */
    createAudit: (data: NewAuditPayload, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<RegistryShipmentResponse>>({
        url: `/Audit`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Busqueda general de Reporte de Auditorias
     *
     * @tags shipment
     * @name registryShipment
     * @request POST:/api/v1/AuditReport/GeneralSearch
     * @secure
     */
    auditReportGeneralSearch: (
      data: GeneralSearchAuditReport,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AuditReport[]>>({
        url: `/AuditReport/GeneralSearch`,
        method: 'POST',
        data,
        // paramsSerializer: () =>
        //   qs.stringify(data, {
        //     arrayFormat: 'repeat'
        //   }),
        ...config
      }),

    /* Datos para reporte de auditoria
     * @tags auditReport
     * @name getAuditReportData
     * @request POST:/api/v1/AuditReport
     * @secure
     */
    getAuditReportData: (
      data: AuditReportRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AuditReportData>>({
        url: `/AuditReport`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Elimina un reporte de Auditoria
     *
     * @tags AuditReport
     * @name deleteAudit
     * @request DELETE:/api/v1/AuditReport
     * @secure
     */
    deleteAudit: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<RegistryShipmentResponse>>({
        url: `/AuditReport`,
        method: 'DELETE',
        data: { AuditId: id },
        ...config
      })
  }
};
